import { CardHolderServiceInterface } from "interfaces/card-holder-service.ts"
import { capitalize } from "lib/utils"
import { DriverSchema } from "mock/schemas"
import { BaseMmsMockService } from "services/mms/mock/entities-services/BaseMockService"
import { CommunicationService } from "services/mms/mock/CommunicationService"
import { DriverInfo } from "types/list-card-holder"
import { FetchMockProps } from "types/mms-base"

export class MockCardHolderService
    extends BaseMmsMockService
    implements CardHolderServiceInterface{
    constructor(communicationService: CommunicationService) {
        super(communicationService)
    }

    private mapDriver = (driver: DriverSchema): DriverInfo => {
        return {
            id: driver.id,
            name: `${capitalize(driver.firstName)} ${capitalize(driver.lastName)}`,
            lastName: driver.lastName,
            firstName: driver.firstName,
            createdAt: driver.createAt,
            email: driver.email,
            active: driver.active,
            numberOfCards: this.communicationService.numberOfRfidCardsPerDriver(
                driver.id
            ),
            isEmailVerified: driver.email_verified,
        }
    }

    useGetListCardHolders = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const driversFromSelfEntity = this.communicationService.drivers
                    .map((d) => ({ ...d }))
                    .filter((d) => d.entity_id === user?.entity_id)
                return {
                    data: driversFromSelfEntity.map(this.mapDriver),
                }
            },
        }

        return this.get(datas)
    }

    useGetAllActiveCardHolders = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const driversFromSelfEntity = this.communicationService.drivers
                    .map((d) => ({ ...d }))
                    .filter((d) => {
                        return (
                            d.entity_id === user?.entity_id && d.active === true
                        )
                    }).length
                return {
                    data: driversFromSelfEntity,
                }
            },
        }
        return this.get(datas)
    }

    useAddCardHolder = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
            controllerFn: ({ body, user }) => {
                const { lastName, firstName, email, isEmailVerified } =
                    body as DriverInfo
                const newDriver: DriverSchema = {
                    lastName,
                    firstName,
                    email,
                    active: true,
                    id: (
                        this.communicationService.drivers.length + 1
                    ).toString(),
                    entity_id: user?.entity_id || "",
                    createAt: new Date().toLocaleString(),
                    email_verified: isEmailVerified,
                }
                const otherDriverWithSameEmail =
                    this.communicationService.drivers.find(
                        (d) => d.email === email
                    )
                if (otherDriverWithSameEmail) {
                    return {
                        error: "Email already exists",
                        status: 400,
                    }
                }
                this.communicationService.drivers.push(newDriver)
                return {
                    data: this.mapDriver(newDriver),
                }
            },
        }
        return this.post(datas)
    }

    useRemoveDriver = (driver_id: string): FetchApiProps => {
        this.communicationService.drivers =
            this.communicationService.drivers.filter((d) => d.id !== driver_id)
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
        }
        return this.delete(datas)
    }

    useActivateCardHolder = (driver_id: string): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 201,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const driver = this.communicationService.drivers.find(
                    (d) => d.id === driver_id
                )
                if (!driver || driver.entity_id !== user?.entity_id) {
                    return { status: 404, error: "Driver not found" }
                }
                driver.active = true
            },
        }
        return this.put(datas)
    }

    useDeactivateCardHolder = (driver_id: string): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 201,
            delay: this.delay,
            controllerFn: ({ user }) => {
                const driver = this.communicationService.drivers.find(
                    (d) => d.id === driver_id
                )
                this.communicationService.rfidCards.forEach((card, idx) => {
                    if (
                        card.entity_id === user?.entity_id &&
                        card.driver_id === driver_id
                    ) {
                        this.communicationService.rfidCards[idx].active = false
                        this.communicationService.rfidCards[idx].description =
                            "Conducteur désactivé"
                    }
                })
                if (!driver || driver.entity_id !== user?.entity_id) {
                    return { status: 404, error: "Driver not found" }
                }
                driver.active = false
            },
        }
        return this.put(datas)
    }

    useUpdateCardHolder = (driver_id: string): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 201,
            delay: this.delay,
            controllerFn: ({ body, user }) => {
                const driver = this.communicationService.drivers.find(
                    (d) => d.id === driver_id
                )
                if (!driver || driver.entity_id !== user?.entity_id) {
                    return { status: 404, error: "Driver not found" }
                }
                const { lastName, firstName } = body as DriverInfo
                driver.lastName = lastName
                driver.firstName = firstName
            },
        }
        return this.put(datas)
    }

    useGetEnableVidAutocharge = (): FetchApiProps => {
        const data: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
        }
        return this.get(data)
    }

    usePostAutochargeToVehicle = (): FetchApiProps => {
        const datas: FetchMockProps = {
            mockStatus: 200,
            delay: this.delay,
        }
        return this.post(datas)
    }

}
